import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Plus, Trash2, Save, RotateCcw, Loader2, Upload, History, X } from "lucide-react";
import { api, fileUrl } from "../lib/api";

const SECTION_LABELS = {
  home: "Homepagina", diensten: "Diensten (homepagina)", reviews: "Reviews",
  werkplaats: "Werkplaats", avia: "AVIA-tankstation", over: "Over ons",
  contact: "Contact", bedrijf: "Bedrijfsgegevens", afbeeldingen: "Afbeeldingen",
  seo: "SEO / Vindbaarheid (Google)",
};

const humanize = (k) =>
  String(k).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bL1\b/, "Regel 1").replace(/\bL2\b/, "Regel 2");

const emptyLike = (sample) => {
  if (typeof sample === "object" && sample !== null && !Array.isArray(sample)) {
    return Object.fromEntries(Object.keys(sample).map((k) => [k, typeof sample[k] === "object" ? emptyLike(sample[k]) : ""]));
  }
  if (Array.isArray(sample)) return [];
  return "";
};

const ImageInput = ({ value, onChange, label }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.path);
      toast.success("Afbeelding geüpload — vergeet niet op te slaan.");
    } catch {
      toast.error("Upload mislukt.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      {label && <span className="block text-xs font-semibold text-text-muted mb-1.5">{humanize(label)}</span>}
      <div className="flex gap-3 items-start">
        <div className="w-28 h-20 bg-background border border-border rounded overflow-hidden shrink-0">
          {value ? <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" /> : null}
        </div>
        <div className="flex-1 min-w-0">
          <label className="inline-flex items-center gap-2 rounded-full bg-primary text-white text-sm font-semibold px-4 py-2 cursor-pointer hover:bg-primary-light transition-colors">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Nieuwe foto
            <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`image-upload-${label}`} />
          </label>
          <input
            className="mt-2 w-full bg-surface border border-border px-3 py-2 text-xs outline-none focus:border-primary rounded"
            value={value} onChange={(e) => onChange(e.target.value)} placeholder="of plak een afbeeldings-URL"
          />
        </div>
      </div>
    </div>
  );
};

// Recursieve render-functie (gewone functie i.p.v. component om react-refresh stack-overflow te voorkomen)
function renderField(value, onChange, label, image) {
  if (image && typeof value === "string") {
    return <ImageInput value={value} onChange={onChange} label={label} />;
  }

  if (typeof value === "string") {
    const long = value.length > 60;
    return (
      <label className="block">
        {label && <span className="block text-xs font-semibold text-text-muted mb-1.5">{humanize(label)}</span>}
        {long ? (
          <textarea className="w-full bg-surface border border-border px-3 py-2 text-sm outline-none focus:border-primary rounded resize-y min-h-[70px]"
            value={value} onChange={(e) => onChange(e.target.value)} rows={3} />
        ) : (
          <input className="w-full bg-surface border border-border px-3 py-2 text-sm outline-none focus:border-primary rounded"
            value={value} onChange={(e) => onChange(e.target.value)} />
        )}
      </label>
    );
  }

  if (Array.isArray(value)) {
    return (
      <div>
        {label && <p className="text-xs font-bold uppercase tracking-wide text-primary mb-2">{humanize(label)}</p>}
        <div className="space-y-3">
          {value.map((item, i) => (
            <div key={i} className="flex gap-2 items-start bg-background border border-border rounded p-3">
              <div className="flex-1 min-w-0">
                {typeof item === "object" && item !== null ? (
                  <div className="grid gap-2">
                    {Object.keys(item).map((k) => (
                      <div key={k}>
                        {renderField(item[k], (nv) => { const c = [...value]; c[i] = { ...c[i], [k]: nv }; onChange(c); }, k, image)}
                      </div>
                    ))}
                  </div>
                ) : (
                  renderField(item, (nv) => { const c = [...value]; c[i] = nv; onChange(c); }, undefined, image)
                )}
              </div>
              <button type="button" aria-label="Verwijderen"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
                className="shrink-0 mt-1 text-red-500 hover:bg-red-50 rounded p-1.5 transition-colors">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => onChange([...value, emptyLike(value[0] ?? "")])}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
          <Plus size={15} /> Item toevoegen
        </button>
      </div>
    );
  }

  if (typeof value === "object" && value !== null) {
    return (
      <div className="grid gap-3">
        {label && <p className="text-xs font-bold uppercase tracking-wide text-primary">{humanize(label)}</p>}
        {Object.keys(value).map((k) => (
          <div key={k}>
            {renderField(value[k], (nv) => onChange({ ...value, [k]: nv }), k, image)}
          </div>
        ))}
      </div>
    );
  }

  return null;
}

const SEO_PAGES = {
  home: { label: "Homepagina", path: "" },
  occasions: { label: "Occasions", path: "occasions" },
  werkplaats: { label: "Werkplaats", path: "werkplaats" },
  avia: { label: "AVIA", path: "avia" },
  over: { label: "Over ons", path: "over-ons" },
  contact: { label: "Contact", path: "contact" },
  afspraak: { label: "Afspraak", path: "afspraak" },
};

const Counter = ({ n, ideal }) => (
  <span className={`text-[11px] font-semibold ${n > ideal ? "text-red-500" : "text-text-muted"}`}>{n}/{ideal}</span>
);

const SeoSection = ({ value, onChange }) => (
  <div className="grid gap-5">
    <p className="text-sm text-text-muted">Zo verschijnt je pagina in Google. Houd de titel ≤ 60 en de omschrijving ≤ 155 tekens voor het beste resultaat.</p>
    {Object.keys(value).map((k) => {
      const page = SEO_PAGES[k] || { label: k, path: k };
      const seo = value[k] || { title: "", description: "" };
      const set = (field) => (v) => onChange({ ...value, [k]: { ...seo, [field]: v } });
      return (
        <div key={k} className="border border-border rounded-lg p-4 bg-background" data-testid={`seo-page-${k}`}>
          <p className="font-display font-bold text-base mb-3">{page.label}</p>
          <label className="block mb-3">
            <span className="flex justify-between text-xs font-semibold text-text-muted mb-1"><span>Paginatitel</span><Counter n={(seo.title || "").length} ideal={60} /></span>
            <input data-testid={`seo-title-${k}`} className="w-full bg-surface border border-border px-3 py-2 text-sm outline-none focus:border-primary rounded"
              value={seo.title || ""} onChange={(e) => set("title")(e.target.value)} />
          </label>
          <label className="block mb-4">
            <span className="flex justify-between text-xs font-semibold text-text-muted mb-1"><span>Omschrijving</span><Counter n={(seo.description || "").length} ideal={155} /></span>
            <textarea data-testid={`seo-desc-${k}`} rows={2} className="w-full bg-surface border border-border px-3 py-2 text-sm outline-none focus:border-primary rounded resize-y"
              value={seo.description || ""} onChange={(e) => set("description")(e.target.value)} />
          </label>
          {/* Google-preview */}
          <div className="bg-white border border-border rounded-lg p-4" data-testid={`seo-preview-${k}`}>
            <p className="text-xs text-[#202124]">Automobielbedrijf Stoof</p>
            <p className="text-[13px] text-[#006621] leading-tight">stoofvinkeveen.nl{page.path ? " › " + page.path : ""}</p>
            <p className="text-[#1a0dab] text-lg leading-snug truncate">{seo.title || "Paginatitel…"}</p>
            <p className="text-sm text-[#4d5156] leading-snug line-clamp-2">{seo.description || "De omschrijving verschijnt hier…"}</p>
          </div>
        </div>
      );
    })}
  </div>
);

const SectionPanel = ({ name, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const isImage = name === "afbeeldingen";
  return (
    <div className="border border-border bg-surface" data-testid={`content-section-${name}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-background/60 transition-colors"
        data-testid={`content-toggle-${name}`}>
        <span className="font-display font-bold text-lg">{SECTION_LABELS[name] || humanize(name)}</span>
        <ChevronDown size={20} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-6 pt-1 border-t border-border grid gap-4">
          {name === "seo"
            ? <SeoSection value={value} onChange={onChange} />
            : renderField(value, onChange, undefined, isImage)}
        </div>
      )}
    </div>
  );
};

const HistoryPanel = ({ onClose, onRestore }) => {
  const [versions, setVersions] = useState(null);
  useEffect(() => {
    api.get("/content/history").then((r) => setVersions(r.data)).catch(() => setVersions([]));
  }, []);
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="history-panel">
      <div className="bg-surface w-full max-w-md max-h-[80vh] overflow-auto rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-surface">
          <h3 className="font-display font-bold text-lg">Wijzigingshistorie</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-background rounded"><X size={18} /></button>
        </div>
        {versions === null ? (
          <div className="flex items-center gap-2 text-text-muted p-6"><Loader2 className="animate-spin" size={16} /> Laden…</div>
        ) : versions.length === 0 ? (
          <p className="text-text-muted text-sm p-6">Nog geen eerdere versies. Zodra u teksten opslaat, verschijnt hier de historie.</p>
        ) : (
          <ul className="divide-y divide-border">
            {versions.map((v) => (
              <li key={v.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
                <span className="text-sm text-text-main">{fmt(v.archived_at)}</span>
                <button onClick={() => onRestore(v.id)} data-testid={`history-restore-${v.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border text-sm font-semibold px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">
                  <RotateCcw size={13} /> Terugzetten
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export const ContentEditor = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => toast.error("Kon teksten niet laden."));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", content);
      toast.success("Opgeslagen. Ververs de website om de wijzigingen te zien.");
    } catch {
      toast.error("Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Alle teksten én afbeeldingen terugzetten naar standaard? Uw wijzigingen gaan verloren.")) return;
    try {
      const r = await api.post("/content/reset");
      setContent(r.data);
      toast.success("Teruggezet naar standaard.");
    } catch {
      toast.error("Terugzetten mislukt.");
    }
  };

  const restore = async (id) => {
    try {
      const r = await api.post(`/content/restore/${id}`);
      setContent(r.data);
      setShowHistory(false);
      toast.success("Vorige versie teruggezet.");
    } catch {
      toast.error("Terugzetten mislukt.");
    }
  };

  if (!content) {
    return <div className="flex items-center gap-2 text-text-muted py-16"><Loader2 className="animate-spin" size={18} /> Teksten laden…</div>;
  }

  const SaveBtn = ({ testid }) => (
    <button onClick={save} disabled={saving} data-testid={testid}
      className="inline-flex items-center gap-2 rounded-full bg-primary text-white font-semibold px-5 py-2.5 text-sm hover:bg-primary-light transition-colors disabled:opacity-60">
      {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Opslaan
    </button>
  );

  return (
    <div data-testid="content-editor">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-display font-bold text-2xl">Teksten &amp; afbeeldingen</h2>
          <p className="text-text-muted text-sm mt-1">Pas teksten, foto's en SEO aan. Klik een sectie open, wijzig en sla op.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button onClick={() => setShowHistory(true)} data-testid="content-history"
            className="inline-flex items-center gap-2 rounded-full border border-border font-semibold px-4 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors">
            <History size={15} /> Historie
          </button>
          <button onClick={reset} data-testid="content-reset"
            className="inline-flex items-center gap-2 rounded-full border border-border font-semibold px-4 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors">
            <RotateCcw size={15} /> Standaard
          </button>
          <SaveBtn testid="content-save" />
        </div>
      </div>

      <div className="grid gap-3">
        {Object.keys(content).map((name) => (
          <SectionPanel key={name} name={name} value={content[name]}
            onChange={(nv) => setContent({ ...content, [name]: nv })} />
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <SaveBtn testid="content-save-bottom" />
      </div>

      {showHistory && <HistoryPanel onClose={() => setShowHistory(false)} onRestore={restore} />}
    </div>
  );
};
